/**
 * PATH: packages/email-hub/src/_internal/components/ApiAuthGate.tsx
 * FILE_ID: EMAILHUB-INTERNAL-API-AUTH-GATE-001
 * PURPOSE: Publish stable auth/tenant context before Email Hub children run.
 * WHY: Host auth protects routes; this gate binds package transport and RL-01.
 *
 * Hub-agnostic adapter that replaces SAM's tenant-specific ApiAuthGate.
 *
 * SAM's original gate did SSO exchange + organisation_slug body
 * construction tied to /company/<slug>/ URLs. That's the wrong shape
 * for email.quotech.ai (a top-level standalone hub). The standalone
 * hub already runs ProtectedRoute / SSO / session resolution at the
 * @quotech/auth layer ABOVE this gate — by the time EmailLayout
 * mounts, we already have a valid session.
 *
 * Behavioural contract preserved for in-package consumers:
 *   - `useApiAuth()` returns `{ session: { user, organisation } | null }`
 *   - `<ApiAuthGate>` wraps children; rendering does not gate auth
 *     (host's ProtectedRoute does that). It DOES sync the access token
 *     into the package's axios instance so direct API calls
 *     (BulkLinkToJobModal, RecipientAutocomplete, etc.) authenticate.
 *
 * Hosts:
 *   - apps/email: passes session from @quotech/auth's useAuth + accessToken
 *     from useAuthStore.getState().accessToken
 *   - apps/sam (future): keeps its existing in-tree ApiAuthGate; this
 *     package's gate is only used when the package is mounted standalone
 */
import {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  type ReactNode,
} from 'react'
import { setEmailTenantId } from '@quotech/tenant-shared/email/api'
import { setAccessToken, clearAccessToken } from '../hooks/useApi'
import {
  applyEmailOrgSwitchBoundary,
  finishEmailOrgSwitchBoundary,
} from '../../feature/lib/orgRebind'

export interface SessionUser {
  id: number
  email?: string
  first_name?: string
  last_name?: string
  name?: string
}

export interface SessionOrganisation {
  id: number | null
  slug?: string
  name?: string
}

export interface AuthSession {
  user: SessionUser | null
  organisation: SessionOrganisation | null
}

interface AuthCtx {
  session: AuthSession | null
  switchOrganisation?: (
    orgId: number,
    orgName?: string,
  ) => Promise<void> | void
}

const Ctx = createContext<AuthCtx>({ session: null })

export interface ApiAuthGateProps {
  session: AuthSession | null
  accessToken: string | null
  switchOrganisation?: AuthCtx['switchOrganisation']
  children: ReactNode
}

/** Value-stable session identity. Hosts build the session INLINE per
 *  render (`buildSession(...)` in EmailHubV2 / KanbanHostBridge /
 *  apps/email App.tsx), so the prop is a fresh object every parent
 *  render even when nothing changed. BUG-2026-07-15-MEMBERS-FETCH-STORM:
 *  that identity churn invalidated downstream `useMemo`s keyed on
 *  `session` (members adapter) and refired their fetch effects on every
 *  shell re-render — a request storm under a slow backend. We keep the
 *  PREVIOUS object whenever the primitive fields are unchanged so
 *  context consumers get a referentially stable session. */
function sameSession(a: AuthSession | null, b: AuthSession | null): boolean {
  if (a === b) return true
  if (!a || !b) return false
  return (
    (a.user?.id ?? null) === (b.user?.id ?? null) &&
    (a.user?.email ?? null) === (b.user?.email ?? null) &&
    (a.user?.name ?? null) === (b.user?.name ?? null) &&
    (a.organisation?.id ?? null) === (b.organisation?.id ?? null) &&
    (a.organisation?.slug ?? null) === (b.organisation?.slug ?? null) &&
    (a.organisation?.name ?? null) === (b.organisation?.name ?? null)
  )
}

export function ApiAuthGate({
  session,
  accessToken,
  switchOrganisation,
  children,
}: ApiAuthGateProps) {
  useEffect(() => {
    if (accessToken) setAccessToken(accessToken)
    else clearAccessToken()
  }, [accessToken])

  // BUG-2026-07-15-EMAIL-TENANT-PUBLISH-RACE — publish the tenant id
  // DURING RENDER, not in an effect. React runs child effects BEFORE
  // parent effects, so every host that published via useEffect
  // (EmailHubV2 TenantBridge, apps/email TenantBridge, EmailLayout)
  // let the inbox's first fetch fire with `_tenantId = null`: the
  // request went out WITHOUT `?tenant=`, the BE fell back to the
  // user's FIRST membership org, and on a multi-org operator the
  // cold search/list ran against the wrong (largest) mailbox —
  // symptom "This is taking longer than usual" on the first search
  // after a fresh mount. The setter writes a module singleton (no
  // React state), so a render-phase write is safe and idempotent;
  // StrictMode double-render just writes the same value twice.
  // Hosts' own useEffect publications remain as belt-and-braces for
  // org SWITCHES after mount.
  const orgIdForTenant = session?.organisation?.id ?? null
  const publishedTenantRef = useRef<number | null | undefined>(undefined)
  const pendingOrgCleanupRef = useRef(false)
  if (publishedTenantRef.current !== orgIdForTenant) {
    if (publishedTenantRef.current !== undefined) {
      applyEmailOrgSwitchBoundary(orgIdForTenant)
      pendingOrgCleanupRef.current = true
    } else {
      setEmailTenantId(orgIdForTenant)
    }
    publishedTenantRef.current = orgIdForTenant
  }
  useLayoutEffect(() => {
    if (!pendingOrgCleanupRef.current) return
    pendingOrgCleanupRef.current = false
    finishEmailOrgSwitchBoundary(orgIdForTenant)
  }, [orgIdForTenant])

  const stableRef = useRef<AuthSession | null>(session)
  if (!sameSession(stableRef.current, session)) {
    stableRef.current = session
  }
  const stableSession = stableRef.current
  const ctxValue = useMemo(
    () => ({ session: stableSession, switchOrganisation }),
    [stableSession, switchOrganisation],
  )

  return <Ctx.Provider value={ctxValue}>{children}</Ctx.Provider>
}

export function useApiAuth(): AuthCtx {
  return useContext(Ctx)
}

export type SsoDiagnostic = unknown
